import type { App } from '@hangar/shared';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ImportFields } from './ImportFields';
import { ImportPreview } from './ImportPreview';
import { importSubmitState } from './importModel';
import { useImportForm, type ImportFormApi } from './useImportForm';
import type { ScopeSelection } from './ScopePicker';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  projectId: string;
  scope: ScopeSelection;
  apps: App[] | undefined;
}

/** Paste a dotenv document → dry run (per-key actions + warnings such as LITERAL_BACKSLASH_N) → import. */
export function ImportSheet({ open, onOpenChange, projectId, scope, apps }: Props) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" data-testid="env-import-sheet" className="w-full gap-0 p-0" aria-describedby="env-import-desc">
        {open ? <ImportForm projectId={projectId} scope={scope} apps={apps} onDone={() => onOpenChange(false)} /> : null}
      </SheetContent>
    </Sheet>
  );
}

function ImportForm({ projectId, scope, apps, onDone }: Omit<Props, 'open' | 'onOpenChange'> & { onDone: () => void }) {
  const f = useImportForm({ projectId, scope, apps, onDone });
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="env-import-form">
      <SheetHeader>
        <SheetTitle>Import .env</SheetTitle>
        <SheetDescription id="env-import-desc" className="text-xs">
          Target: <span className="font-mono text-foreground">{f.targetLabel}</span> · dotenv syntax (quotes, export, comments, multiline)
        </SheetDescription>
      </SheetHeader>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-5">
        <ImportFields f={f} />
        {f.preview ? <ImportPreview preview={f.preview} /> : null}
        <ProblemAlert error={f.problem} testId="env-import-problem" />
      </div>
      <ImportFooter f={f} onCancel={onDone} />
    </div>
  );
}

function ImportFooter({ f, onCancel }: { f: ImportFormApi; onCancel: () => void }) {
  const submit = importSubmitState(f.preview);
  return (
    <SheetFooter className="flex-row flex-wrap justify-end border-t">
      <Button type="button" variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
      <Button type="button" variant="outline" data-testid="env-import-preview-button" loading={f.isPending && !f.preview} onClick={() => void f.run(true)}>
        <Icon name="visibility" size={18} />
        Preview
      </Button>
      <Button type="button" data-testid="env-import-submit" disabled={submit.disabled} title={submit.title} loading={f.isPending && Boolean(f.preview)} onClick={() => void f.run(false)}>
        <Icon name="upload" size={18} />
        Import
      </Button>
    </SheetFooter>
  );
}
